"use client"

import { useEffect, useState } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Trash2, User } from "lucide-react"
import { toast } from "react-hot-toast"
import Pagination from "../../Common/Pagination"

export default function RegisteredFacesTab({ refreshSignal = 0 }) {
  const [list, setList] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const fetchRegistered = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(API_ENDPOINTS.faceRegistered, {
        params: {
          paginate: true,
          page: currentPage,
          limit: itemsPerPage,
        },
      })
      setList(res.data?.data ?? [])
      setTotal(Number(res.data?.total ?? res.data?.count ?? 0))
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load registered faces")
      setList([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [refreshSignal])

  useEffect(() => {
    fetchRegistered()
  }, [refreshSignal, currentPage, itemsPerPage])

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete registered face for ${name || "this user"}? This cannot be undone.`)) return
    try {
      setDeletingId(userId)
      await axiosInstance.delete(API_ENDPOINTS.faceDelete(userId))
      toast.success("Face data deleted successfully")
      setList((prev) => prev.filter((r) => r.userId !== userId))
      setTotal((prev) => Math.max(0, prev - 1))
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete")
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Registered Faces</CardTitle>
        <Alert className="mt-2 bg-muted/50">
          <AlertDescription>
            Teachers and staff with registered faces appear here. Deleting removes their face data; they can register again.
          </AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : list.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No registered faces yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Photo</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead className="w-[80px]">Role</TableHead>
                <TableHead className="w-[100px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.userId}>
                  <TableCell>
                    <div className="h-12 w-12 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                      {row.registrationImage ? (
                        <img src={row.registrationImage} alt="" className="h-full w-full object-cover" />
                      ) : row.profileImage ? (
                        <img src={row.profileImage} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <User className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{row.name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{row.employeeId || "—"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${row.role === "staff" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                      {row.role === "staff" ? "Staff" : "Teacher"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(row.userId, row.name)}
                      disabled={deletingId === row.userId}
                    >
                      {deletingId === row.userId ? (
                        <span className="animate-pulse">Deleting...</span>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          currentPageCount={list.length}
          itemLabel="faces"
        />
      </CardContent>
    </Card>
  )
}
